import React, { Component } from "react";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import cx from "classnames";
import { withTranslation } from "react-i18next";
import { Form, Input, Label, Message } from "semantic-ui-react";
import Address, {
  modelPropTypes as addressModelPropTypes,
  schemaPropTypes as addressSchemaPropTypes,
  addressValidation,
} from "~/components/element/Address/Address";
import Country from "~/models/Country";
import Tour from "~/models/Tour";
import Search from "~/components/element/Search";
import ButtonLink from "~/components/element/ButtonLink";
import PromiseButton from "~/components/element/PromiseButton";
import Map from "~/components/element/Map";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import ValidateSchema from "~/utils/ReactReformed/ValidateSchema";
import CreateTourStore from "../stores/CreateTourStore";
import CustomerHasUrbanimmersiveSpecialPricingCard from "~/applications/tourbuilder/components/CustomerHasUrbanimmersiveSpecialPricingCard";
import "../styles/CreateTour.less";

@observer
class CreateTour extends Component {
  constructor(props) {
    super(props);

    this.domRef = React.createRef();

    this.state = {
      mapWidth: 0,
      showCoCustomer: false,
    };
  }

  componentDidMount() {
    // calculate the width, we'll take 100% of the parent container, and height of 3/4 of its width.
    const dom = this.domRef.current;
    const width = dom.clientWidth;

    this.setState({ mapWidth: width });
  }

  onCustomerSearchChange(property, value) {
    const { setProperty, createTourStore } = this.props;

    // whenever the search value changes we'll also clear out the customer (if previously selected)
    setProperty(property, value);

    switch (property) {
      case "customer":
        createTourStore.setSelectedCustomer(null);

        break;
      case "coCustomer":
        createTourStore.setSelectedCoCustomer(null);
        break;
      default:
        throw new Error(`Expected customer property, got ${property}`);
    }
  }

  onCustomerResultSelect(property, customer) {
    const { createTourStore } = this.props;

    switch (property) {
      case "customer":
        createTourStore.setSelectedCustomer(customer);

        break;
      case "coCustomer":
        createTourStore.setSelectedCoCustomer(customer);
        break;
      default:
        throw new Error(`Expected customer property, got ${property}`);
    }
  }

  toggleCoCustomer() {
    const { setProperty, createTourStore } = this.props;
    const { showCoCustomer } = this.state;

    setProperty("coCustomer", null);
    createTourStore.setSelectedCoCustomer(null);
    this.setState({ showCoCustomer: !showCoCustomer });
  }

  allValid() {
    const { createTourStore, schema, isDirty } = this.props;

    return (
      createTourStore.selectedAddressResult !== null &&
      createTourStore.selectedCustomer !== null &&
      schema.isValid &&
      isDirty
    );
  }

  submit() {
    const { tour, model, createTourStore, showError, onTourCreated, onTourUpdated } = this.props;

    if (!this.allValid()) {
      return Promise.resolve();
    }

    const inEditMode = tour && tour.tourId;

    return createTourStore
      .submit(model)
      .then(response => {
        if (inEditMode) {
          onTourUpdated(response.data.tour);
        } else {
          onTourCreated(response.data.tour.tourId);
        }
      })
      .catch(error => showError(error.message));
  }

  render() {
    const {
      countries,
      tour,
      blurred,
      model,
      isDirty,
      setModel,
      setProperty,
      schema,
      bindInput,
      getAggregatedError,
      onBlur: onBlur2,
      createTourStore,
      customText,
      createCustomerCallback,
      showCustomerSection,
      timezoneOptions,
    } = this.props;
    const { mapWidth, showCoCustomer } = this.state;
    const waiting = createTourStore.isSaving || countries.length === 0;
    const { errorMessages, isMyUrbanimmersive } = createTourStore;
    const inEditMode = tour !== null;

    const { subscriptionStore, customerStatisticStore, customerHasUrbanimmersiveSpecialPricing } = createTourStore;
    const { activeSubscriptions } = subscriptionStore;
    const { customerStatistic } = customerStatisticStore;

    return (
      // Show a loading icon if form is submitting or if countries are not loaded yet
      <Form className="create-tour" loading={waiting}>
        {/* Only used to calculate the total width available within the form so it can be used for the Map's width */}
        <div ref={this.domRef} />
        <Address
          countries={countries}
          model={model}
          isDirty={isDirty}
          setModel={setModel}
          schema={schema}
          bindInput={bindInput}
          serverValidationErrors={errorMessages}
          hasSelectedResult={!!createTourStore.selectedAddressResult}
          addressResults={createTourStore.addressResults}
          setAddressResults={results => createTourStore.setAddressResults(results)}
          setSelectedAddressResult={result => createTourStore.setSelectedAddressResult(result)}
        />
        <br />
        {showCustomerSection && (
          <Form.Group widths="equal">
            <Form.Field
              className={cx("lookup-or-create", { error: blurred.customer && !createTourStore.selectedCustomer })}
            >
              <label htmlFor="customer">* Customer</label>
              <Search
                {...(() => {
                  const { name, value, onBlur } = bindInput("customer");
                  return { name, value, onBlur };
                })()}
                fluid
                id="customer"
                placeholder="Look up existing customer"
                name="customer"
                value={model.customer || ""}
                asyncSearchCallback={value => createTourStore.searchCustomer(value)}
                onSearchChange={event => this.onCustomerSearchChange("customer", event.target.value)}
                onResultSelect={(_event, data) => this.onCustomerResultSelect("customer", data.result)}
              />
              <ButtonLink onClick={() => createCustomerCallback("customer")}>create a customer</ButtonLink>|
              <ButtonLink onClick={() => this.toggleCoCustomer()}>
                {showCoCustomer ? "hide co-customer" : "add a co-customer"}
              </ButtonLink>
            </Form.Field>
            <Form.Field
              className={cx("lookup-or-create co-customer", {
                show: showCoCustomer,
                // Error if the user has entered a search value but hasn't selected a result.
                error: blurred.coCustomer && (model.coCustomer || "").length > 0 && !createTourStore.selectedCoCustomer,
              })}
            >
              <label htmlFor="cocustomer">Co Customer</label>
              <Search
                id="cocustomer"
                {...(() => {
                  const { name, value, onBlur } = bindInput("coCustomer");
                  return { name, value, onBlur };
                })()}
                fluid
                placeholder="Lookup existing co-customer"
                asyncSearchCallback={value => createTourStore.searchCustomer(value)}
                onSearchChange={event => this.onCustomerSearchChange("coCustomer", event.target.value)}
                onResultSelect={(_event, data) => this.onCustomerResultSelect("coCustomer", data.result)}
              />
              <ButtonLink onClick={() => createCustomerCallback("coCustomer")}>create a co-customer</ButtonLink>
            </Form.Field>
          </Form.Group>
        )}
        {customerHasUrbanimmersiveSpecialPricing && (
          <CustomerHasUrbanimmersiveSpecialPricingCard
            isLoading={subscriptionStore.isLoading || customerStatisticStore.isLoading}
            subscription={activeSubscriptions.length > 0 ? activeSubscriptions[0] : null}
            statistic={customerStatistic}
          />
        )}
        <div className="shoot-details">
          <Form.Checkbox
            disabled={isMyUrbanimmersive}
            checked={model.shootHasImmersive3dTour}
            label="I have an Immersive 3D Tour."
            onChange={(event, data) => setProperty("shootHasImmersive3dTour", data.checked)}
          />
          {model.shootHasImmersive3dTour && (
            <>
              <Message>
                <Message.List>
                  <Message.Item>
                    <small>
                      For Residential Only: In order to guarantee the 24 hours turnaround, please fill in the fields
                      below.
                    </small>
                  </Message.Item>
                  <Message.Item>
                    <small>
                      For Commercial Only: In order to improve the reception time of your tour, please fill in the
                      fields below.
                    </small>
                  </Message.Item>
                </Message.List>
              </Message>
              <Form.Group widths="equal">
                <Form.Input
                  type="datetime-local"
                  label="* Shoot Date/Time"
                  id="shoot-dts"
                  {...bindInput("shootDts", schema, errorMessages)}
                  error={getAggregatedError("shootDts", schema, errorMessages)}
                />
                <Form.Select
                  fluid
                  id="shoot-timezone"
                  label="&nbsp;"
                  loading={timezoneOptions.length === 0}
                  placeholder="Select a timezone"
                  value={model.shootTimezone || null}
                  options={timezoneOptions}
                  onChange={(_event, data) => setProperty("shootTimezone", data.value)}
                  error={getAggregatedError("shootTimezone", schema, errorMessages)}
                />
              </Form.Group>
              <Form.Field>
                <label htmlFor="shoot-size">* Estimated shoot size</label>
                <Input
                  fluid
                  label={{ basic: true, content: "sq. ft." }}
                  labelPosition="right"
                  id="shoot-size"
                  name="shootSize"
                  placeholder="2000"
                  value={model.shootSize}
                  onBlur={onBlur2}
                  onChange={e => setProperty("shootSize", e.target.value.replace(/\D/g, ""))}
                  error={!!getAggregatedError("shootSize", schema, errorMessages)}
                />
                {typeof getAggregatedError("shootSize", schema, errorMessages) === "object" && (
                  <Label pointing prompt>
                    {getAggregatedError("shootSize", schema, errorMessages).content}
                  </Label>
                )}
              </Form.Field>
            </>
          )}
        </div>
        <br />
        <Map
          latLng={createTourStore.addressLatLng}
          width={mapWidth}
          heightAspectRation={6 / 16}
          onMarkerPositionChange={latLng => createTourStore.setMarkerLatLng(latLng)}
        />
        <div className="create-tour-actions">
          <PromiseButton
            // show loading icon while store is loading.
            // This will help improving UX, and probably by the time the user is done filling
            // the tour form this store would had finished loading.
            loading={createTourStore.loading}
            disabled={!this.allValid()}
            primary
            onClick={() => this.submit()}
          >
            {inEditMode ? customText.updateButton || "Update Tour" : "Create Tour"}
          </PromiseButton>
        </div>
      </Form>
    );
  }
}

CreateTour.propTypes = {
  // specific to react-reformed
  model: PropTypes.shape({
    ...addressModelPropTypes,
    customer: PropTypes.string,
    coCustomer: PropTypes.string,
    shootHasImmersive3dTour: PropTypes.bool,
    shootDts: PropTypes.string,
    shootTimezone: PropTypes.string,
    shootSize: PropTypes.string,
  }).isRequired,
  blurred: PropTypes.shape({
    customer: PropTypes.bool,
    coCustomer: PropTypes.bool,
  }),
  schema: PropTypes.shape({
    isValid: PropTypes.bool,
    fields: PropTypes.shape({
      ...addressSchemaPropTypes,
      customer: PropTypes.object,
      coCustomer: PropTypes.object,
    }).isRequired,
  }).isRequired,
  setModel: PropTypes.func.isRequired,
  setProperty: PropTypes.func.isRequired,
  bindInput: PropTypes.func.isRequired,
  getAggregatedError: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  // everything else.
  isDirty: PropTypes.bool,
  countries: PropTypes.arrayOf(PropTypes.instanceOf(Country)),
  createTourStore: PropTypes.instanceOf(CreateTourStore),
  tour: PropTypes.instanceOf(Tour),
  createCustomerCallback: PropTypes.func.isRequired,
  showCustomerSection: PropTypes.bool,
  onTourCreated: PropTypes.func,
  onTourUpdated: PropTypes.func,
  customText: PropTypes.shape({
    updateButton: PropTypes.string,
  }),
  timezoneOptions: PropTypes.array,
};

CreateTour.defaultProps = {
  tour: null,
  customText: {},
  showCustomerSection: true,
  onTourCreated: tourId => {
    window.location.href = `/panel/tour/edit/${tourId}?tabView=tabImages`;
  },
};

const WrappedCreateTour = ValidateSchema({
  ...addressValidation,
  customer: {
    required: true,
  },
  shootDts: {
    callback: (value, model) => {
      // if shootHasImmersive3dTour is toggled on, then a value for shootDts is required
      if (model.shootHasImmersive3dTour && !value) {
        return "required";
      }

      return null;
    },
  },
  shootTimezone: {
    callback: (value, model) => {
      // if shootHasImmersive3dTour is toggled on, then a value for shootDts is required
      if (model.shootHasImmersive3dTour && !value) {
        return "required";
      }

      return null;
    },
  },
  shootSize: {
    callback: (value, model) => {
      // if shootHasImmersive3dTour is toggled on, then a value for shootDts is required
      if (model.shootHasImmersive3dTour && !value) {
        return "required";
      }

      return null;
    },
  },
})(CreateTour);

export default withTranslation()(ErrorWrapper(WrappedCreateTour));
